import React, { startTransition, StrictMode, useEffect } from "react";
import { setDefaultOptions } from "date-fns";
import { fr as dateFnsFr, enCA } from "date-fns/locale";
import i18next from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import posthog from "posthog-js";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { HydratedRouter } from "react-router/dom";
import { getInitialNamespaces } from "remix-i18next/client";

import i18n, { createResources } from "@acme/remix/i18n";

import en from "~/locales/en";
import fr from "~/locales/fr";

const Posthog = ({ children }: React.PropsWithChildren) => {
  useEffect(() => {
    posthog.init(import.meta.env.VITE_POSTHOG_PROJECT_API_KEY, {
      api_host: "https://us.i.posthog.com",
    });
  }, []);
  return <>{children}</>;
};

async function hydrate() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(I18nextBrowserLanguageDetector) // Setup a client-side language detector
    .init({
      ...i18n,
      ns: getInitialNamespaces(),
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ["htmlTag"],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
      resources: createResources({ fr, en }),
    });

  // Detect the user's language
  const detectedLanguage = i18next.language;

  // Set date-fns locale based on the detected language
  const locale = detectedLanguage === "fr" ? dateFnsFr : enCA; // Add more locale mappings if needed
  setDefaultOptions({ locale });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <Posthog>
            <HydratedRouter />
          </Posthog>
        </StrictMode>
      </I18nextProvider>,
    );
  });
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
if (window.requestIdleCallback) {
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  window.setTimeout(hydrate, 1);
}
